/**
 * The fixed words of a card on the net or on paper, besides the business's own (data/card.json, from
 * SpeiseKarte's CARD_WORDS and AS_OF, 01.11.2026), and the tags that tell guests about the diet. The
 * business's dictionary and the translation of its texts stay with the app.
 */
import data from "../data/card.json" with { type: "json" };
import type { Lang } from "./langs.ts";

/** The words a card prints besides the business's own, in one language. */
export interface CardWords {
  /** The default foot, while the business kept it: "Alle Preise in Euro inkl. MwSt.". */
  footer: string;
  food: string;
  drinks: string;
  allergens: string;
  additives: string;
  or: string;
  choice: string;
  soldOut: string;
  recommendations: string;
  symbols: string;
  /** „Seite 1 von 2“, with {n} and {of}. */
  page: string;
  /** A set menu without a title of its own. */
  setMenu: string;
  /** The card without a title. */
  card: string;
  imprint: string;
  privacy: string;
  /** The page when no dish stands on the menu. */
  empty: string;
  /** Before the date of the card: „Stand“. */
  asOf: string;
}

export const CARD_WORDS: Readonly<Record<Lang | "de", Readonly<CardWords>>> = data.words;

/** „Stand“ in each language, before the date of a card. */
export const AS_OF: Readonly<Record<Lang | "de", string>> = Object.fromEntries(Object.entries(CARD_WORDS).map(([l, w]) => [l, w.asOf])) as Record<Lang | "de", string>;

/** The tags that tell guests about the diet, as German cards write them (lower case). */
export const DIET_TAGS = ["vegan", "vegetarisch", "glutenfrei", "laktosefrei", "scharf"] as const;
export type DietTag = (typeof DIET_TAGS)[number];

/** By language, the diet's tags: "vegetarisch" → "vegetarian". */
export const DIET_TAG_WORDS: Readonly<Record<Lang, Readonly<Record<DietTag, string>>>> = data.dietTags;

export const isDietTag = (tag: string): tag is DietTag => (DIET_TAGS as readonly string[]).includes(tag.toLowerCase());

/** A diet's tag in a language, in any case („Vegan“); undefined for other tags, which stay the business's. */
export function dietTagIn(tag: string, lang: Lang): string | undefined {
  const t = tag.toLowerCase();
  return isDietTag(t) ? DIET_TAG_WORDS[lang][t] : undefined;
}
